/**
 * El chip del modo de transporte de una carga: icono + palabra.
 *
 * Hereda la forma de `AereaBadge` —que es de donde viene el aéreo— para que la
 * tabla no tenga dos idiomas de chip. Lo que decide qué modo es, incluida la
 * regla de que el aéreo manda, vive en `modoTransporteDe`: aquí sólo se pinta.
 *
 * Un modo que no conocemos se pinta como TEXTO, sin chip ni icono. Si mañana el
 * notebook emite «MULTIMODAL», saldrá la palabra tal cual en vez de un icono
 * equivocado o una celda vacía.
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { MODO_CHIP, esModoConocido, type ModoConocido } from '@/utils/cargasSalida'

/** Los tres iconos, a 24×24 y en trazo, como el resto de la tabla. */
const TRAZOS: Record<ModoConocido, ReactNode> = {
  AEREO: (
    <path d="M10.2 3.3a1.7 1.7 0 0 1 3.6 0L14 10l7 4v2l-7-2-.4 4.4 2.4 1.8V22l-4-1-4 1v-1.8l2.4-1.8L10 14l-7 2v-2l7-4z" />
  ),
  MARITIMO: (
    <>
      <path d="M4.5 14.5 6 9h12l1.5 5.5" />
      <path d="M2 18.5c1.3 0 1.3 1.5 2.7 1.5s1.3-1.5 2.7-1.5 1.3 1.5 2.6 1.5 1.3-1.5 2.7-1.5 1.3 1.5 2.7 1.5 1.3-1.5 2.6-1.5" />
      <path d="M12 9V5" />
    </>
  ),
  TERRESTRE: (
    <>
      <path d="M14 17V6H2v11h1.5" />
      <path d="M14 9h4l3 3.5V17h-1.5" />
      <circle cx="6.5" cy="17.5" r="1.8" />
      <circle cx="16.5" cy="17.5" r="1.8" />
    </>
  ),
}

export function ChipModoTransporte({ modo, dark }: { modo: string | null; dark: boolean }) {
  const { t } = useTranslation()
  if (!modo) return null

  // El valor es la clave de traducción, y el propio valor es su defecto: así un
  // modo nuevo del origen sale legible en vez de como una ruta de i18n.
  const texto = t(`cargasSalida.tabla.modos.${modo}`, { defaultValue: modo })

  if (!esModoConocido(modo)) {
    return <span className="truncate" title={texto}>{texto}</span>
  }

  const tono = dark ? MODO_CHIP[modo].oscuro : MODO_CHIP[modo].claro
  return (
    <span
      title={texto}
      className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${tono}`}
    >
      <svg
        className="w-2.5 h-2.5 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {TRAZOS[modo]}
      </svg>
      {texto}
    </span>
  )
}
